import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trainingRequestsAPI, partnersAPI, purchaseOrdersAPI, taxonomyAPI } from '../services/api';
import { TIMEZONES } from '../constants/options';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, CalendarPlus } from 'lucide-react';
import { EntityCustomFields } from '../components/shared/EntityCustomFields';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const DELIVERY_MODES = [
  { value: 'virtual', label: 'Virtual' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'hybrid', label: 'Hybrid' },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export const TrainingProjectFormPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [allPos, setAllPos] = useState([]);
  const [showAddSessionPrompt, setShowAddSessionPrompt] = useState(false);
  const [newProjectId, setNewProjectId] = useState(null);
  
  const [partnerId, setPartnerId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requestorName, setRequestorName] = useState('');
  const [requestorEmail, setRequestorEmail] = useState('');
  const [requestDate, setRequestDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryMode, setDeliveryMode] = useState('virtual');
  const [preferredDates, setPreferredDates] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [location, setLocation] = useState('');
  const [expectedAttendees, setExpectedAttendees] = useState(0);
  const [pmName, setPmName] = useState('');
  
  // Financial fields
  const [approvedPrice, setApprovedPrice] = useState('');
  const [amountInvoiced, setAmountInvoiced] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Taxonomy custom fields
  const [categories, setCategories] = useState([]);
  const [customFieldKeys, setCustomFieldKeys] = useState([]);
  const [customFields, setCustomFields] = useState({});
  const addableCustomCategories = categories.filter(c => !customFieldKeys.includes(c.id));

  useEffect(() => {
    taxonomyAPI.getAll().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    loadPartners();
    loadAllPos();
    // Auto-populate PM name from logged-in user
    if (user?.name) {
      setPmName(user.name);
    }
  }, [user]);

  const loadPartners = async () => {
    try {
      const partnersData = await partnersAPI.getAll();
      setPartners(partnersData);
    } catch (error) {
      console.error('Failed to load partners:', error);
      toast.error('Failed to load partners');
    }
  };

  const loadAllPos = async () => {
    try {
      const posData = await purchaseOrdersAPI.getAll({ active_only: true });
      setAllPos(posData);
    } catch (error) {
      console.error('Failed to load Delivery POs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!partnerId) {
      toast.error('Please select a partner');
      return;
    }
    if (!title) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        partner_id: partnerId,
        title,
        description,
        requestor_name: requestorName,
        requestor_email: requestorEmail,
        request_date: requestDate,
        pm_name: pmName,
        course_ids: [],
        custom_courses: [],
        delivery_mode: deliveryMode,
        preferred_dates: preferredDates,
        timezone,
        location,
        expected_attendees: parseInt(expectedAttendees) || 0,
        po_number: poNumber || null,
        approved_price: approvedPrice ? parseFloat(approvedPrice) : null,
        amount_invoiced: amountInvoiced ? parseFloat(amountInvoiced) : null,
        notes,
        custom_fields: Object.keys(customFields).length ? customFields : null,
      };
      
      const project = await trainingRequestsAPI.create(payload, user.id);
      
      toast.success('Training project created successfully');
      setNewProjectId(project.id);
      setShowAddSessionPrompt(true);
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create training project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-project-form">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/training-projects')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold">New Training Project</h1>
          <p className="text-muted-foreground">
            Create a new training delivery project
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
            <CardDescription>Basic information about the training project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="partner">Partner *</Label>
                <Select value={partnerId} onValueChange={setPartnerId}>
                  <SelectTrigger data-testid="partner-select">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="request_date">Request Date</Label>
                <Input
                  id="request_date"
                  type="date"
                  value={requestDate}
                  onChange={(e) => setRequestDate(e.target.value)}
                  data-testid="request-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Azure Fundamentals Training for EY"
                data-testid="title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide details about the training project..."
                rows={3}
                data-testid="description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="requestor_name">Requestor Name</Label>
                <Input
                  id="requestor_name"
                  value={requestorName}
                  onChange={(e) => setRequestorName(e.target.value)}
                  placeholder="John Smith"
                  data-testid="requestor-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requestor_email">Requestor Email</Label>
                <Input
                  id="requestor_email"
                  type="email"
                  value={requestorEmail}
                  onChange={(e) => setRequestorEmail(e.target.value)}
                  placeholder="john.smith@partner.com"
                  data-testid="requestor-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pm_name">Project Manager</Label>
              <Input
                id="pm_name"
                value={pmName}
                onChange={(e) => setPmName(e.target.value)}
                placeholder="Project manager name"
                data-testid="pm-name-input"
              />
              <p className="text-xs text-muted-foreground">Auto-populated from your account. You may edit if needed.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Delivery Details</CardTitle>
            <CardDescription>How and when the training will be delivered</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="delivery_mode">Delivery Mode</Label>
                <Select value={deliveryMode} onValueChange={setDeliveryMode}>
                  <SelectTrigger data-testid="delivery-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIVERY_MODES.map(mode => (
                      <SelectItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expected_attendees">Expected Attendees</Label>
                <Input
                  id="expected_attendees"
                  type="number"
                  min="0"
                  value={expectedAttendees}
                  onChange={(e) => setExpectedAttendees(e.target.value)}
                  data-testid="expected-attendees-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger data-testid="timezone-select">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferred_dates">Session Date</Label>
              <Input
                id="preferred_dates"
                value={preferredDates}
                onChange={(e) => setPreferredDates(e.target.value)}
                placeholder="e.g., Week of March 15, 2025"
                data-testid="preferred-dates-input"
              />
            </div>

            {(deliveryMode === 'in_person' || deliveryMode === 'hybrid') && (
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., Microsoft Campus, Redmond"
                  data-testid="location-input"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Financials</CardTitle>
            <CardDescription>Approved budget and PO invoicing details for this project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="approved_price">Approved Price (USD)</Label>
                <Input
                  id="approved_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={approvedPrice}
                  onChange={(e) => setApprovedPrice(e.target.value)}
                  placeholder="0.00"
                  data-testid="approved-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount_invoiced">Amount Invoiced (USD)</Label>
                <Input
                  id="amount_invoiced"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountInvoiced}
                  onChange={(e) => setAmountInvoiced(e.target.value)}
                  placeholder="0.00"
                  data-testid="amount-invoiced-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="po_number">PO# Invoiced in</Label>
              <Select value={poNumber || 'none'} onValueChange={(v) => setPoNumber(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="po-number-select">
                  <SelectValue placeholder="Select PO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Assigned</SelectItem>
                  {allPos.map(po => (
                    <SelectItem key={po.id} value={po.po_number}>
                      <div className="flex items-center justify-between w-full">
                        <span>{po.po_number}</span>
                        <Badge variant="outline" className="text-xs ml-2">{formatCurrency(po.available_balance)} available</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional information..."
              rows={4}
              data-testid="notes-input"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Fields</CardTitle>
            <CardDescription>Add optional fields from the Taxonomy master lists (Solution Areas, Asset Types, etc.)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <EntityCustomFields
              entityType="training_request"
              values={customFields}
              onChange={setCustomFields}
              isSuperAdmin={user?.role === 'superadmin'}
              currentUser={user}
              testPrefix="delivery-custom-field"
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button 
            type="button" 
            variant="outline"
            onClick={() => navigate('/training-projects')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-lime text-lime-foreground hover:bg-lime/90"
            data-testid="submit-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Creating...' : 'Create Project'}
          </Button>
        </div>
      </form>

      {/* Soft Prompt: Add First Session */}
      <Dialog open={showAddSessionPrompt} onOpenChange={setShowAddSessionPrompt}>
        <DialogContent className="max-w-sm" data-testid="add-session-prompt-dialog">
          <DialogHeader>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-lime/15 mx-auto mb-2">
              <CalendarPlus className="w-6 h-6 text-lime-700" />
            </div>
            <DialogTitle className="text-center">Project Created!</DialogTitle>
            <DialogDescription className="text-center">
              Would you like to add your first training session now? You can always add sessions later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <Button
              className="bg-lime text-lime-foreground hover:bg-lime/90"
              onClick={() => navigate(`/training-projects/${newProjectId}`, { state: { tab: 'sessions' } })}
              data-testid="add-session-now-btn"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Add Session Now
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/training-projects/${newProjectId}`)}
              data-testid="skip-session-btn"
            >
              Skip for now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingProjectFormPage;
